import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { db } from "../firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { motion } from "framer-motion";
import paymentQr from "../../assets/paymentqr.jpeg";
import eBooks from "./EbookData.js";
import paymode from "../../assets/sadd.jpg";
const PaymentPage = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [book, setBook] = useState(null);
  const [emailID, setEmailID] = useState("");

  // Firestore collection reference
  const paymentsCollectionRef = collection(db, "payments");
  useEffect(() => {
    // Find the book based on URL parameter
    const selectedBook = eBooks.find((book) => book.id === parseInt(bookId));
    if (selectedBook) {
      setBook(selectedBook);
    } else {
      toast.error("Book not found");
      navigate("/");
    }
  }, [bookId, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!userName.trim() || !transactionId.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setIsLoading(true);

    try {
      // Create payment data
      const paymentData = {
        bookId: book.id,
        bookTitle: book.title,
        amount: book.price,
        emailID,
        userName,
        transactionId,
        createdAt: serverTimestamp(),
        status: "pending",
      };

      // Save to Firestore
      const docRef = await addDoc(paymentsCollectionRef, paymentData);

      toast.success("Payment details submitted successfully!");
      navigate(`/confirmation/${transactionId}`);
    } catch (error) {
      console.error("Error saving payment: ", error);
      toast.error("Failed to save payment details. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!book) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
        <div className="text-center p-8 bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 shadow-2xl">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-cyan-500 mx-auto mb-6"></div>
          <p className="text-gray-300">Loading book details...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-slate-900 to-black py-12 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500"
          >
            Complete Your Purchase
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mt-4 text-gray-400"
          >
            Secure payment for your premium eBook
          </motion.p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
          {/* Left Column - Book Details */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 shadow-2xl p-6"
          >
            <div className="flex items-center mb-8">
              <div className="relative">
                <img
                  src={book.image}
                  alt={book.title}
                  className="w-24 h-32 object-cover rounded-xl border-2 border-gray-600"
                />
                <div className="absolute -top-2 -right-2 bg-amber-500 text-white text-xs font-bold py-1 px-2 rounded-full">
                  Premium
                </div>
              </div>
              <div className="ml-6">
                <h2 className="text-xl font-bold text-white">{book.title}</h2>
                <p className="text-gray-400 mt-1">eBook • Premium Edition</p>
                <div className="mt-4 flex items-center">
                  <span className="text-3xl font-bold text-cyan-400">
                    {book.price}
                  </span>
                  <span className="ml-2 text-sm text-gray-400 line-through">
                    ₹{parseInt(book.price.replace("₹", "")) + 200}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-semibold text-white mb-4">
                Order Summary
              </h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Subtotal</span>
                  <span className="text-white">{book.price}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Discount</span>
                  <span className="text-green-400">-₹200</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Tax</span>
                  <span className="text-white">₹0</span>
                </div>
                <div className="flex justify-between pt-4 border-t border-gray-700">
                  <span className="text-lg font-bold text-white">Total</span>
                  <span className="text-xl font-bold text-cyan-400">
                    {book.price}
                  </span>
                </div>
              </div>
            </div>

            <div className="mt-8">
              <h3 className="text-lg font-semibold text-white mb-4">
                What you'll get
              </h3>
              <ul className="space-y-2">
                <li className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/10 flex items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-cyan-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-300">Instant PDF download</span>
                </li>
                <li className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/10 flex items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-cyan-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-300">
                    Lifetime access to updates
                  </span>
                </li>
                <li className="flex items-center">
                  <div className="w-6 h-6 rounded-full bg-cyan-500/10 flex items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-cyan-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M5 13l4 4L19 7"
                      ></path>
                    </svg>
                  </div>
                  <span className="text-gray-300">Premium support</span>
                </li>
              </ul>
            </div>
            <div className="mt-5 relative p-0.5 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 animate-gradient-x">
              <div className="bg-white/20 backdrop-blur-lg p-6 rounded-2xl border border-white/30 shadow-2xl hover:bg-white/30 transition-all duration-500">
                <img
                  src={paymode}
                  alt="Secure Payment Options"
                  className="w-full h-auto object-contain filter drop-shadow-md"
                />
              </div>
            </div>
          </motion.div>

          {/* Right Column - Payment Details */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700 shadow-2xl p-6"
          >
            <h2 className="text-2xl font-bold text-white mb-6">
              Payment Details
            </h2>

            <div className="bg-gradient-to-br from-gray-900 to-black border border-cyan-500/30 rounded-xl p-6 mb-8">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-cyan-300 mb-2">
                    Scan to Pay
                  </h3>
                  <p className="text-gray-400 text-sm">
                    Use any UPI app to complete payment
                  </p>
                </div>
                <div className="bg-gradient-to-br from-cyan-600 to-blue-700 text-white text-xs font-bold py-1 px-3 rounded-full">
                  RECOMMENDED
                </div>
              </div>

              <div className="flex justify-center my-6">
                <div className="relative">
                  {/* QR Code Container */}
                  <div className="bg-white p-4 rounded-lg shadow-lg">
                    <div className="bg-gray-200 border-2 border-dashed rounded-xl w-48 h-48 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl mb-2">
                          <img src={paymentQr} alt="Qr Code" />
                        </div>
                        {/* <p className="text-gray-600 text-sm">UPI QR Code</p> */}
                      </div>
                    </div>
                  </div>

                  {/* Animated Scan Effect */}
                  <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-cyan-500 to-transparent animate-scan rounded-full"></div>
                </div>
              </div>

              <div className="flex justify-between items-center mt-4">
                <div>
                  <p className="text-gray-400 text-sm">Payment Amount</p>
                  <p className="text-xl font-bold text-white">{book.price}</p>
                </div>
                <div>
                  <p className="text-gray-400 text-sm">UPI ID</p>
                  <p className="text-white font-mono">9464257927@ybl</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="mb-6">
                <label className="block text-gray-400 mb-2">
                  Your Full Name
                </label>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors"
                  placeholder="Enter your name"
                />
              </div>
              <div className="mb-6">
                <label className="block text-gray-400 mb-2">
                  Your Email Id (
                  <span className="text-red-500">
                    eBook Will be sent to this Email id
                  </span>
                  )
                </label>
                <input
                  type="text"
                  value={emailID}
                  onChange={(e) => setEmailID(e.target.value)} // ✅ CORRECTED
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors"
                  placeholder="Enter Email ID"
                />
              </div>

              <div className="mb-8">
                <label className="block text-gray-400 mb-2">
                  Transaction ID / UTR Number
                </label>
                <input
                  type="text"
                  value={transactionId}
                  onChange={(e) => setTransactionId(e.target.value)}
                  className="w-full bg-gray-900 border border-gray-700 rounded-lg py-3 px-4 text-white focus:outline-none focus:ring-2 focus:ring-cyan-500 transition-colors"
                  placeholder="Enter transaction reference"
                />
                <p className="mt-2 text-sm text-gray-500">
                  After payment, enter the transaction ID or UTR number from
                  your banking app
                </p>
              </div>

              <motion.button
                type="submit"
                disabled={isLoading}
                className={`w-full py-4 rounded-xl font-bold text-white transition-all duration-300 ${
                  isLoading
                    ? "bg-gray-700 cursor-not-allowed"
                    : "bg-gradient-to-r from-cyan-600 to-blue-700 shadow-lg"
                }`}
                whileHover={!isLoading ? { scale: 1.02 } : {}}
                whileTap={!isLoading ? { scale: 0.98 } : {}}
              >
                {isLoading ? (
                  <div className="flex items-center justify-center">
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Processing...
                  </div>
                ) : (
                  "Confirm Payment"
                )}
              </motion.button>
            </form>

            <div className="mt-8 pt-6 border-t border-gray-700">
              <h3 className="text-lg font-semibold text-white mb-4">
                Secure Payment
              </h3>
              <div className="flex items-center">
                <div className="bg-green-500/10 p-2 rounded-lg mr-4">
                  <svg
                    className="w-6 h-6 text-green-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                    ></path>
                  </svg>
                </div>
                <p className="text-gray-400">
                  Your payment is securely processed. We do not store your
                  banking details.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Watermark */}
      <div className="absolute bottom-4 right-4 text-gray-700 text-sm">
        Premium eBooks • Secure Payment
      </div>
    </div>
  );
};

// Confirmation Page Component

export default PaymentPage;
